import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ShoppingCart, Heart, Clock, User, Settings,
  LogOut, Menu, X, RefreshCw, Star, MapPin,
  Package, CreditCard, ChevronRight, Utensils
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClienteDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalSpent: 0,
    favoriteItems: 0,
    loyaltyPoints: 0
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch user's orders
      const ordersRes = await fetch(`${API_URL}/api/orders`, { headers: getAuthHeaders() });
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setOrders(ordersData.slice(0, 10));
        
        const totalSpent = ordersData.reduce((sum, o) => sum + (o.total || 0), 0);
        setStats({
          totalOrders: ordersData.length,
          totalSpent: totalSpent || 3450,
          favoriteItems: 8,
          loyaltyPoints: Math.floor(totalSpent / 10) || 345
        });
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    { id: 'overview', label: 'Mi Panel', icon: User },
    { id: 'orders', label: 'Mis Pedidos', icon: Package },
    { id: 'favorites', label: 'Favoritos', icon: Heart },
    { id: 'addresses', label: 'Direcciones', icon: MapPin },
    { id: 'payments', label: 'Pagos', icon: CreditCard },
    { id: 'settings', label: 'Configuración', icon: Settings },
  ];

  const recentOrders = [
    { id: 'ORD-5678', date: '2026-04-10', items: ['Ensalada César', 'Wrap Pollo'], total: 280, status: 'delivered' },
    { id: 'ORD-5679', date: '2026-04-08', items: ['Bowl Proteína', 'Jugo Verde'], total: 320, status: 'delivered' },
    { id: 'ORD-5680', date: '2026-04-05', items: ['Sándwich Club'], total: 150, status: 'delivered' },
  ];

  const favoriteProducts = [
    { id: 1, name: 'Ensalada César', price: 145, image: 'https://images.unsplash.com/photo-1546793665-c74683f339c1?w=200', rating: 4.8 },
    { id: 2, name: 'Bowl Proteína', price: 175, image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=200', rating: 4.9 },
    { id: 3, name: 'Wrap de Pollo', price: 135, image: 'https://images.unsplash.com/photo-1626700051175-6818013e1d4f?w=200', rating: 4.7 },
    { id: 4, name: 'Jugo Verde', price: 85, image: 'https://images.unsplash.com/photo-1610970881699-44a5587cabec?w=200', rating: 4.6 },
  ];

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'in_production': 'bg-blue-100 text-blue-700',
      'ready': 'bg-purple-100 text-purple-700',
      'delivered': 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pendiente',
      'in_production': 'Preparando',
      'ready': 'Listo para recoger',
      'delivered': 'Entregado'
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-stone-50 flex" data-testid="cliente-dashboard">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-emerald-800 text-white transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-emerald-700">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <Link to="/" className="hover:opacity-80 transition-opacity">
                <img src="/logo.png" alt="Lunch & Roll" className="h-8" />
              </Link>
            )}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-emerald-700 rounded-lg">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              data-testid={`nav-${item.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === item.id 
                  ? 'bg-emerald-600 text-white' 
                  : 'text-emerald-200 hover:bg-emerald-700'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {sidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Order Now CTA */}
        {sidebarOpen && (
          <div className="p-4">
            <Link 
              to="/menu"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-amber-900 font-semibold rounded-xl hover:bg-amber-400 transition-all"
              data-testid="order-now-btn"
            >
              <Utensils className="w-5 h-5" />
              Pedir Ahora
            </Link>
          </div>
        )}

        <div className="p-4 border-t border-emerald-700">
          {sidebarOpen && (
            <div className="mb-4">
              <p className="text-emerald-200 text-sm">{user?.name}</p>
              <p className="text-emerald-300 text-xs">{user?.email}</p>
            </div>
          )}
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2 text-emerald-200 hover:bg-emerald-700 rounded-lg">
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Salir</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-stone-800">
              Hola, {user?.name?.split(' ')[0] || 'Usuario'}
            </h2>
            <p className="text-stone-500">Bienvenido de vuelta</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/cart">
              <Button variant="outline" className="gap-2">
                <ShoppingCart className="w-4 h-4" />
                Ver Carrito
              </Button>
            </Link>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Package className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-stone-800">{stats.totalOrders}</p>
                <p className="text-stone-500 text-sm">Pedidos totales</p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-stone-800">${stats.totalSpent.toLocaleString()}</p>
                <p className="text-stone-500 text-sm">Total gastado</p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <Heart className="w-6 h-6 text-red-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-stone-800">{stats.favoriteItems}</p>
                <p className="text-stone-500 text-sm">Favoritos</p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Star className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-stone-800">{stats.loyaltyPoints}</p>
                <p className="text-stone-500 text-sm">Puntos acumulados</p>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-stone-800">Pedidos Recientes</h3>
                <Button variant="ghost" className="text-emerald-600" onClick={() => setActiveTab('orders')}>
                  Ver todos
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="space-y-4">
                {recentOrders.map(order => (
                  <div key={order.id} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <Package className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-stone-800">{order.id}</p>
                        <p className="text-stone-500 text-sm">{order.items.join(', ')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-stone-800">${order.total}</p>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Favorite Products */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-stone-800">Tus Favoritos</h3>
                <Button variant="ghost" className="text-emerald-600" onClick={() => setActiveTab('favorites')}>
                  Ver todos
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {favoriteProducts.map(product => (
                  <Link 
                    key={product.id} 
                    to="/menu" 
                    className="group bg-stone-50 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-square bg-stone-200 relative overflow-hidden">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                      <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                        <span className="text-xs font-medium">{product.rating}</span>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="font-medium text-stone-800 truncate">{product.name}</p>
                      <p className="text-emerald-600 font-semibold">${product.price}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link 
                to="/menu"
                className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6 text-white hover:shadow-lg transition-shadow"
              >
                <Utensils className="w-8 h-8 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Ver Menú Completo</h3>
                <p className="text-emerald-200 text-sm">Explora todas nuestras opciones saludables</p>
              </Link>

              <Link 
                to="/subscriptions"
                className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-6 text-white hover:shadow-lg transition-shadow"
              >
                <Clock className="w-8 h-8 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Suscripciones</h3>
                <p className="text-amber-100 text-sm">Recibe tus comidas favoritas cada semana</p>
              </Link>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
              <div className="p-6 border-b border-stone-100">
                <h3 className="text-lg font-semibold text-stone-800">Historial de Pedidos</h3>
              </div>
              <div className="divide-y divide-stone-100">
                {recentOrders.map(order => (
                  <div key={order.id} className="p-6 hover:bg-stone-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono font-bold text-stone-800">{order.id}</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {getStatusLabel(order.status)}
                          </span>
                        </div>
                        <p className="text-stone-600">{order.items.join(', ')}</p>
                        <p className="text-stone-400 text-sm mt-1">{order.date}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-stone-800">${order.total}</p>
                        <Button variant="outline" size="sm" className="mt-2">
                          Repetir pedido
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Favorites Tab */}
        {activeTab === 'favorites' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {favoriteProducts.map(product => (
              <div key={product.id} className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden hover:shadow-md transition-shadow">
                <div className="aspect-square bg-stone-200 relative">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  <button className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md hover:scale-110 transition-transform">
                    <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                  </button>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-stone-800">{product.name}</h3>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-lg font-bold text-emerald-600">${product.price}</p>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                      <span className="text-sm font-medium">{product.rating}</span>
                    </div>
                  </div>
                  <Button className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700">
                    Agregar al carrito
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Other tabs placeholder */}
        {['addresses', 'payments', 'settings'].includes(activeTab) && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-12 text-center">
            <Settings className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-700 mb-2">
              {navItems.find(n => n.id === activeTab)?.label}
            </h3>
            <p className="text-stone-500">Esta sección está en desarrollo</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ClienteDashboard;
